import { FileUploadSignature } from '@app/core/services/api-client.generated';
import { createReducer, on } from '@ngrx/store';
import { projectDetailActions } from '@core/actions/project-detail.actions';

export const UploadStatus = {
  idle: 'idle',
  error: 'error',
  success: 'success',
  uploading: 'uploading',
  processing: 'processing',
} as const;
export type UploadStatus = (typeof UploadStatus)[keyof typeof UploadStatus];

/** One search's reference-file upload, as the create-search dialog and the wizard show it. */
export interface SearchUIState {
  status: UploadStatus;
  progress: number | null;
  errorMessage: string | null;
  responseCode: number | null;
  signature: FileUploadSignature;
  /** The owning UI closed/reset while this upload was still in flight. */
  discardWhenSettled?: boolean;
}

export const initialSearchUIState: SearchUIState = {
  status: UploadStatus.idle,
  progress: null,
  errorMessage: null,
  responseCode: null,
  signature: {
    uploadHeaders: { '': '' },
    signature: '',
    uploadUrl: '',
  },
};

/**
 * Upload state per search id (#4658). It used to be one global slot that every upload wrote and
 * every dialog reset, so a second upload (after Run in background) overwrote, and reset, the
 * first one's state. A search with no entry is idle.
 */
export interface SearchUploadsState {
  bySearchId: Readonly<Record<string, SearchUIState>>;
}

export const initialSearchUploadsState: SearchUploadsState = { bySearchId: {} };

function update(
  state: SearchUploadsState,
  searchId: string,
  patch: Partial<SearchUIState>
): SearchUploadsState {
  const current = state.bySearchId[searchId];
  // Only startSearchImport creates entries. Late outcomes must not resurrect discarded uploads.
  if (!current) return state;
  if (
    current.discardWhenSettled &&
    (patch.status === UploadStatus.success ||
      patch.status === UploadStatus.error)
  ) {
    return remove(state, searchId);
  }
  return {
    ...state,
    bySearchId: { ...state.bySearchId, [searchId]: { ...current, ...patch } },
  };
}

function remove(
  state: SearchUploadsState,
  searchId: string
): SearchUploadsState {
  return {
    ...state,
    bySearchId: Object.fromEntries(
      Object.entries(state.bySearchId).filter(([id]) => id !== searchId)
    ),
  };
}

export const searchUIReducer = createReducer(
  initialSearchUploadsState,
  // The file has reached SyRF (S3 answered 200). "success" means sent, not imported: the import
  // job reports parsing, saving and completion.
  on(
    projectDetailActions.searchReferenceFileSent,
    (state, props): SearchUploadsState =>
      update(state, props.searchId, {
        status: UploadStatus.success,
        progress: 100,
        errorMessage: null,
        responseCode: 200,
      })
  ),
  on(
    projectDetailActions.searchUploadProgressUpdate,
    (state, props): SearchUploadsState =>
      update(state, props.searchId, {
        status:
          props.progress !== null && props.progress >= 100
            ? UploadStatus.processing
            : UploadStatus.uploading,
        progress: props.progress,
        errorMessage: null,
        responseCode: null,
      })
  ),
  on(
    projectDetailActions.startSearchImport,
    (state, props): SearchUploadsState => ({
      ...state,
      bySearchId: {
        ...state.bySearchId,
        [props.addSearchData.searchId]: {
          ...initialSearchUIState,
          status: UploadStatus.uploading,
          progress: 0,
        },
      },
    })
  ),
  on(
    projectDetailActions.searchUploadError,
    (state, props): SearchUploadsState =>
      update(state, props.searchId, {
        status: UploadStatus.error,
        errorMessage: props.message,
        responseCode: props.code,
      })
  ),
  on(
    projectDetailActions.stopSearchImportWaiting,
    (state, { searchId }): SearchUploadsState =>
      searchId in state.bySearchId ? remove(state, searchId) : state
  ),
  on(
    projectDetailActions.resetSearchUploadState,
    (state, props): SearchUploadsState => {
      if (!props.searchId || !(props.searchId in state.bySearchId)) {
        return state;
      }
      const searchId = props.searchId;
      const current = state.bySearchId[searchId];
      if (
        current.status === UploadStatus.uploading ||
        current.status === UploadStatus.processing
      ) {
        return update(state, searchId, { discardWhenSettled: true });
      }
      return remove(state, searchId);
    }
  )
);
